<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { uuid } from 'vue3-uuid'
import { useRouter } from 'vue-router'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import DashboardLayout from '@/onenetwork/layouts/DashboardLayout.vue'
import { Icon } from '@iconify/vue'
import { useEstateProjectListStore } from '@/modules/estate-project/stores/estate-project-list-store'
import {
  initialEstateProjectValues,
  createDefaultInfoRows,
} from '@/modules/estate-project/helper/estate-project-form'
import { estateProjectSchema } from '@/modules/estate-project/helper/estate-project-schema'
import ProjectInfoRowsEditor from '@/modules/estate-project/components/project-info-rows-editor.vue'
import ProjectDescriptionEditor from '@/modules/estate-project/components/project-description-editor.vue'
import ProjectGalleryEditor from '@/modules/estate-project/components/project-gallery-editor.vue'
import InvestorPickerWithCreatePanel from '@/modules/estate-project/components/investor-picker-with-create-panel.vue'
import ProjectCoordinateMapPopup from '@/modules/estate-project/components/project-coordinate-map-popup.vue'
import ImagePicker from '@/onenetwork/components/image_picker/ImagePicker.vue'
import type {
  EstateProjectFormValues,
  EstateProjectGeoJsonPolygon,
  EstateProjectMedia,
} from '@/modules/estate-project/types/estate-project-types'

interface GooglePlaceLike {
  geometry?: {
    location: {
      lat: () => number
      lng: () => number
    }
  }
  formatted_address?: string
}

const router = useRouter()
const listStore = useEstateProjectListStore()

const resolver = yupResolver(estateProjectSchema)
const formRef = ref()
const initialValues = ref({
  ...initialEstateProjectValues,
  project_information: createDefaultInfoRows(),
})

const autocompleteRef = ref<google.maps.places.Autocomplete | null>(null)
const autocompleteListenerRef = ref<google.maps.MapsEventListener | null>(null)
const autocompleteRetryTimerRef = ref<number | null>(null)

const loading = computed(() => listStore.action_type === 'CREATING')
const logoUuid = ref(uuid.v4().toString())

const logoValue = computed(() => {
  return (formRef.value?.fields?.logo?.states?.value || null) as EstateProjectMedia | null
})

const markerPosition = computed(() => {
  const lat = Number(formRef.value?.fields?.lat?.states?.value)
  const lng = Number(formRef.value?.fields?.lng?.states?.value)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
})

const polygonGeojsonValue = computed(() => {
  return (formRef.value?.fields?.polygon_geojson?.states?.value || null) as EstateProjectGeoJsonPolygon | null
})

const handlePolygonConfirm = ({ polygon_geojson }: { polygon_geojson: EstateProjectGeoJsonPolygon }) => {
  formRef.value?.setFieldValue('polygon_geojson', polygon_geojson)
}

const handlePolygonClear = () => {
  formRef.value?.setFieldValue('polygon_geojson', null)
}

const handleProjectMarkerChange = ({ lat, lng }: { lat: number; lng: number }) => {
  formRef.value?.setFieldValue('lat', lat)
  formRef.value?.setFieldValue('lng', lng)
}

const handleChooseLogo = (payload: any) => {
  const media = payload?.response?.data || payload
  if (!media?.uuid) return
  formRef.value?.setFieldValue('logo', media)
  logoUuid.value = uuid.v4().toString()
}

const handleRemoveLogo = () => {
  formRef.value?.setFieldValue('logo', null)
  logoUuid.value = uuid.v4().toString()
}

const handleSave = () => {
  if (loading.value) return
  formRef.value?.submit()
}

const onSubmit = async (e: any) => {
  const { valid } = e
  if (!valid || loading.value) return

  const values: EstateProjectFormValues = e.values || Object.keys(e.states || {}).reduce((acc: any, key) => {
    acc[key] = e.states[key].value
    return acc
  }, {})

  let created: { id: number } | null = null
  try {
    created = await listStore.createProject(values)
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Không thể tạo dự án'
    alert(message)
    return
  }

  try {
    await listStore.fetchList()
  } catch (e) {
    console.error('Không thể tải lại danh sách dự án sau khi tạo', e)
  }

  if (created?.id) {
    router.push({ name: 'property-projects', query: { detailId: String(created.id) } })
  }
}

const initAutocomplete = async () => {
  await nextTick()
  const formEl = formRef.value?.$el as HTMLElement | undefined
  const addressInput =
    (formEl?.querySelector('input[name="project_address"]') as HTMLInputElement | null) ||
    (formEl?.querySelector('.estate-project-input input') as HTMLInputElement | null)

  if (!addressInput || typeof google === 'undefined' || !google.maps?.places?.Autocomplete) {
    if (autocompleteRetryTimerRef.value) {
      window.clearTimeout(autocompleteRetryTimerRef.value)
    }
    autocompleteRetryTimerRef.value = window.setTimeout(() => {
      void initAutocomplete()
    }, 500)
    return
  }

  if (autocompleteRetryTimerRef.value) {
    window.clearTimeout(autocompleteRetryTimerRef.value)
    autocompleteRetryTimerRef.value = null
  }

  if (!autocompleteRef.value || (autocompleteRef.value as unknown as { gm_accessors_?: { input?: HTMLElement } }).gm_accessors_?.input !== addressInput) {
    autocompleteListenerRef.value?.remove()
    autocompleteListenerRef.value = null
    autocompleteRef.value = new google.maps.places.Autocomplete(addressInput, {
      types: ['geocode'],
      componentRestrictions: { country: 'vn' },
    })
  }

  if (!autocompleteListenerRef.value) {
    autocompleteListenerRef.value = autocompleteRef.value.addListener('place_changed', () => {
      const place = autocompleteRef.value?.getPlace() as GooglePlaceLike | undefined
      if (!place?.geometry) return

      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()

      formRef.value?.setFieldValue('project_address', place.formatted_address)
      formRef.value?.setFieldValue('lat', lat)
      formRef.value?.setFieldValue('lng', lng)
    })
  }
}


onMounted(() => {
  void initAutocomplete()
})

onBeforeUnmount(() => {
  autocompleteListenerRef.value?.remove()
  autocompleteListenerRef.value = null
  if (autocompleteRetryTimerRef.value) {
    window.clearTimeout(autocompleteRetryTimerRef.value)
    autocompleteRetryTimerRef.value = null
  }
})
</script>

<template>
  <DashboardLayout currentRoute="property/projects/create" page-title="Dự Án Bất Động Sản - Tạo Mới">
    <div class="estate-project-create-card">
      <Form
        ref="formRef"
        v-slot="$form"
        :initialValues="initialValues"
        :resolver="resolver"
        @submit="onSubmit"
        class="estate-project-create-form"
      >
        <InputText name="lat" hidden />
        <InputText name="lng" hidden />
        <InputText name="polygon_geojson" hidden />
        <InputText name="logo" hidden />

        <div class="estate-project-create-left">
          <div class="estate-project-form-group">
            <label class="estate-project-label">Logo dự án <span>*</span></label>
            <div class="estate-project-logo-wrap">
              <ImagePicker
                :uuid="logoUuid"
                type="photo"
                folder="real_estate"
                width="120px"
                height="100px"
                :src="logoValue?.url?.medium || logoValue?.name"
                :previewSrc="logoValue?.url?.original"
                upload
                hideUploadPreview
                hideRemove
                previewIcon="sidekickicons:photo-plus-16-solid"
                @action:upload-success="handleChooseLogo"
                @action:choose-image="handleChooseLogo"
              />
              <button
                v-if="logoValue"
                type="button"
                class="estate-project-logo-remove"
                @click="handleRemoveLogo"
              >
                Gỡ logo
              </button>
            </div>
            <span class="estate-project-error">{{ $form?.logo?.error?.message }}</span>
          </div>

          <div class="estate-project-form-group">
            <label class="estate-project-label">Tên dự án <span>*</span></label>
            <InputText name="project_name" class="form-control estate-project-input" placeholder="Nhập tên dự án" />
            <span class="estate-project-error">{{ $form?.project_name?.error?.message }}</span>
          </div>

          <div class="estate-project-form-group">
            <label class="estate-project-label">Địa chỉ dự án <span>*</span></label>
            <InputText name="project_address" class="form-control estate-project-input" placeholder="Nhập địa chỉ dự án" />
            <span class="estate-project-error">{{ $form?.project_address?.error?.message }}</span>
          </div>

          <div class="estate-project-form-group estate-project-map-wrap">
            <ProjectCoordinateMapPopup
              mode="inline"
              :visible="true"
              :editable="true"
              :lat="Number($form.lat?.value || 10.7749)"
              :lng="Number($form.lng?.value || 106.7012)"
              :polygon-geojson="polygonGeojsonValue"
              :show-project-marker="true"
              :project-marker="markerPosition"
              map-class="estate-project-map"
              @confirm="handlePolygonConfirm"
              @clear="handlePolygonClear"
              @project-marker-change="handleProjectMarkerChange"
            />
            <span class="estate-project-error">{{ $form?.polygon_geojson?.error?.message }}</span>
          </div>
        </div>

        <div class="estate-project-create-right">
          <div class="estate-project-section">
            <InvestorPickerWithCreatePanel :form="formRef" :editable="true" />
          </div>

          <div class="estate-project-section">
            <ProjectInfoRowsEditor :form="formRef" :editable="true" />
          </div>

          <div class="estate-project-section">
            <ProjectDescriptionEditor :form="formRef" :editable="true" />
          </div>

          <div class="estate-project-section">
            <ProjectGalleryEditor :form="formRef" :editable="true" />
          </div>
        </div>

        <div class="estate-project-submit-wrap">
          <button type="button" class="estate-project-submit-btn" :disabled="loading" @click="handleSave">
            <Icon icon="stash:plus-light" width="20" height="20" />
            Thêm dự án mới
            <Icon icon="eos-icons:three-dots-loading" width="24" height="24" v-if="loading" />
          </button>
        </div>
      </Form>
    </div>
  </DashboardLayout>
</template>

<style scoped>
.estate-project-create-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.14);
  padding: 32px;
}

.estate-project-create-form {
  display: grid;
  grid-template-columns: minmax(0, 585px) minmax(0, 1fr);
  gap: 32px;
}

.estate-project-create-left,
.estate-project-create-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.estate-project-form-group,
.estate-project-section {
  padding: 6px 0;
}

.estate-project-label {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin-bottom: 10px;
}

.estate-project-label span {
  color: #ff0000;
}

.estate-project-error {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  font-style: italic;
  color: #dc2626;
}

:deep(.estate-project-input),
:deep(.estate-project-input input),
:deep(.estate-project-input .p-inputtext) {
  background: #f7f7f7 !important;
  border: 1px solid #f7f7f7 !important;
  border-radius: 4px;
  min-height: 44px;
}

.estate-project-map {
  width: 100%;
  height: 330px;
  border-radius: 8px;
  overflow: hidden;
}

.estate-project-logo-wrap {
  display: flex;
  gap: 12px;
  align-items: center;
}

.estate-project-logo-remove {
  border: 0;
  background: transparent;
  color: #dc2626;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
}

:deep(.estate-project-map .gm-style-mtc),
:deep(.estate-project-map .gm-bundled-control),
:deep(.estate-project-map .gm-fullscreen-control),
:deep(.estate-project-map .gm-svpc),
:deep(.estate-project-map button[aria-label*='Map']),
:deep(.estate-project-map button[aria-label*='Satellite']),
:deep(.estate-project-map button[aria-label*='Bản đồ']),
:deep(.estate-project-map button[aria-label*='Vệ tinh']),
:deep(.estate-project-map [role='tablist']),
:deep(.estate-project-map [aria-label*='Zoom']),
:deep(.estate-project-map [aria-label*='Phóng to']),
:deep(.estate-project-map [aria-label*='Thu nhỏ']),
:deep(.estate-project-map [aria-label*='Street View']) {
  display: none !important;
}

.estate-project-submit-wrap {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.estate-project-submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  border-radius: 8px;
  background: #0059ff;
  color: #fff;
  padding: 7px 16px;
  height: 38px;
  font-size: 10px;
  font-weight: 700;
}

.estate-project-submit-btn:hover {
  background: #0049d4;
}

@media (max-width: 1280px) {
  .estate-project-create-form {
    grid-template-columns: 1fr;
  }
}
</style>
